import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import axiosInstance from '../../services/axiosInstance';

interface Branch {
  branchId: number;
  branchName: string;
  branchNameAr: string;
}

interface Concept {
  conceptId: number;
  conceptCode: string;
  conceptName: string;
  conceptNameAr: string;
  branchId: number;
  description?: string;
  isActive: boolean;
  displayOrder: number;
}

interface ConceptDialogProps {
  open: boolean;
  concept: Concept | null;
  onClose: (refresh?: boolean) => void;
}

const ConceptDialog: React.FC<ConceptDialogProps> = ({ open, concept, onClose }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [branches, setBranches] = useState<Branch[]>([]);
  const [formData, setFormData] = useState({
    conceptId: 0,
    conceptCode: '',
    conceptName: '',
    conceptNameAr: '',
    branchId: 0,
    description: '',
    isActive: true,
    displayOrder: 1,
  });
  const [loading, setLoading] = useState(false);

  const t = {
    title: concept ? (isRTL ? 'تعديل كونسبت' : 'Edit Concept') : (isRTL ? 'إضافة كونسبت' : 'Add Concept'),
    code: isRTL ? 'الكود' : 'Code',
    name: isRTL ? 'الاسم' : 'Name',
    nameAr: isRTL ? 'الاسم بالعربية' : 'Arabic Name',
    branch: isRTL ? 'الفرع' : 'Branch',
    description: isRTL ? 'الوصف' : 'Description',
    displayOrder: isRTL ? 'الترتيب' : 'Display Order',
    active: isRTL ? 'نشط' : 'Active',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    selectBranch: isRTL ? 'اختر الفرع' : 'Select Branch',
    saveSuccess: isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully',
    requiredFields: isRTL ? 'جميع الحقول مطلوبة' : 'All fields are required'
  };

  useEffect(() => {
    if (open) {
      loadBranches();
      if (concept) {
        setFormData({
          conceptId: concept.conceptId,
          conceptCode: concept.conceptCode,
          conceptName: concept.conceptName,
          conceptNameAr: concept.conceptNameAr,
          branchId: concept.branchId,
          description: concept.description || '',
          isActive: concept.isActive,
          displayOrder: concept.displayOrder,
        });
      } else {
        generateNextCode();
      }
    }
  }, [open, concept]);

  const loadBranches = async () => {
    try {
      const response = await axiosInstance.get('/Branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const generateNextCode = async () => {
    try {
      const response = await axiosInstance.get('/Concepts');
      const concepts = response.data;
      
      let nextCode = 'CONCEPT-001';
      if (concepts.length > 0) {
        const codes = concepts.map((c: any) => c.conceptCode);
        const numbers = codes
          .filter((code: string) => code.startsWith('CONCEPT-'))
          .map((code: string) => parseInt(code.replace('CONCEPT-', '')) || 0);
        
        const maxNumber = Math.max(...numbers, 0);
        const nextNumber = maxNumber + 1;
        nextCode = `CONCEPT-${nextNumber.toString().padStart(3, '0')}`;
      }
      
      setFormData(prev => ({ 
        ...prev,
        conceptId: 0,
        conceptCode: nextCode,
        conceptName: '',
        conceptNameAr: '',
        branchId: branches.length > 0 ? branches[0].branchId : 0,
        description: '',
        isActive: true,
        displayOrder: 1
      }));
    } catch (error) {
      console.error('Failed to generate code:', error);
      setFormData(prev => ({ ...prev, conceptCode: 'CONCEPT-001' }));
    }
  };

  const handleSave = async () => {
    if (!formData.conceptCode.trim() || !formData.conceptName.trim() || !formData.conceptNameAr.trim() || !formData.branchId) {
      toast.error(t.requiredFields);
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        conceptId: formData.conceptId,
        conceptCode: formData.conceptCode,
        conceptName: formData.conceptName,
        conceptNameAr: formData.conceptNameAr,
        branchId: formData.branchId,
        description: formData.description || '',
        isActive: formData.isActive,
        displayOrder: formData.displayOrder
      };

      if (concept) {
        await axiosInstance.put(`/Concepts/${concept.conceptId}`, payload);
      } else {
        await axiosInstance.post('/Concepts', payload);
      }
      
      toast.success(t.saveSuccess);
      onClose(true);
    } catch (error: any) {
      console.error('Save error:', error.response?.data || error);
      toast.error(error.response?.data?.message || (isRTL ? 'فشلت العملية' : 'Operation failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#00796B',
        color: 'white',
        fontWeight: 'bold'
      }}>
        {t.title}
        <IconButton onClick={() => onClose(false)} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField
              label={t.code}
              value={formData.conceptCode}
              onChange={(e) => setFormData({ ...formData, conceptCode: e.target.value })}
              required
              fullWidth
              disabled={!!concept}
            />
            
            <FormControl fullWidth required>
              <InputLabel>{t.branch}</InputLabel>
              <Select
                value={formData.branchId}
                label={t.branch}
                onChange={(e) => setFormData({ ...formData, branchId: e.target.value as number })}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {isRTL ? branch.branchNameAr : branch.branchName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label={t.name}
              value={formData.conceptName}
              onChange={(e) => setFormData({ ...formData, conceptName: e.target.value })}
              required
              fullWidth
            />
            
            <TextField
              label={t.nameAr}
              value={formData.conceptNameAr}
              onChange={(e) => setFormData({ ...formData, conceptNameAr: e.target.value })}
              required
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextField
              label={t.description}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
            <TextField
              label={t.displayOrder}
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 1 })}
              fullWidth
              inputProps={{ min: 1 }}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={t.active}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => onClose(false)} disabled={loading}>
          {t.cancel}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          startIcon={<SaveIcon />} 
          disabled={loading}
          sx={{ bgcolor: '#00796B', '&:hover': { bgcolor: '#00695C' } }}
        >
          {loading ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConceptDialog;